import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, Stack, Chip,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import PaymentIcon from '@mui/icons-material/Payment';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useModules } from '../context/ModulesContext';
import { useLanguage } from '../context/LanguageContext';

const KNOWN_MODULES = ['automations', 'ai_agent', 'filing', 'proposals'];

/**
 * Global listener: when an API call returns 402 from verifyModule the axios
 * interceptor fires a `module:upgrade-required` DOM event. This component
 * catches it and pops a single, calm upgrade dialog so the user understands
 * what happened and can pay in one click.
 *
 * Mount this once inside the authenticated Layout.
 */
const TrialUpgradeListener = () => {
    const [info, setInfo] = useState(null);
    const navigate = useNavigate();
    const { reload } = useModules();
    const { t } = useLanguage();

    useEffect(() => {
        const onUpgradeRequired = (e) => {
            const detail = e.detail || {};
            setInfo({
                moduleName: detail.moduleName || 'module',
                accessLevel: detail.accessLevel || 'view_only',
                message: detail.message,
                trialAutomationCap: detail.trialAutomationCap,
                trialRuleCap: detail.trialRuleCap,
            });
            reload();
        };
        window.addEventListener('module:upgrade-required', onUpgradeRequired);
        return () => window.removeEventListener('module:upgrade-required', onUpgradeRequired);
    }, [reload]);

    if (!info) return null;

    const title = KNOWN_MODULES.includes(info.moduleName) ? t(`modules.${info.moduleName}.title`) : info.moduleName;
    const isCap = !!(info.trialAutomationCap || info.trialRuleCap);

    return (
        <Dialog open={!!info} onClose={() => setInfo(null)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <RocketLaunchIcon color="primary" />
                    <Typography variant="h6" component="span">
                        {isCap ? t('modules.capReachedTitle') : t('modules.trialEndedTitle')}
                    </Typography>
                </Stack>
                <Button onClick={() => setInfo(null)} sx={{ minWidth: 32, p: 0.5 }} color="inherit">
                    <CloseIcon />
                </Button>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Chip label={title} color="primary" variant="outlined" />
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    {info.message || t('modules.trialExhaustedMsg')}
                </Typography>
                {info.trialAutomationCap === 1 && (
                    <Typography variant="body2" color="text.secondary">
                        {t('modules.trialAutomationCapMsg')}
                    </Typography>
                )}
                {info.trialRuleCap === 1 && (
                    <Typography variant="body2" color="text.secondary">
                        {t('modules.trialRuleCapMsg')}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button onClick={() => setInfo(null)}>{t('common.close')}</Button>
                <Button
                    variant="outlined"
                    onClick={() => { setInfo(null); navigate('/quote'); }}
                >
                    {t('modules.getQuote')}
                </Button>
                <Button
                    variant="contained"
                    startIcon={<PaymentIcon />}
                    onClick={() => { setInfo(null); navigate('/billing'); }}
                >
                    {t('modules.upgradeNowBtn')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TrialUpgradeListener;
